/**
 * API métier profil professionnel — préfixe `/api/professionnel` (hors auth).
 * Voir docs/api/HELPEUR_USAGE_professionel.md
 */

import { apiFetch } from "./client";
import { uploadFileToBucket } from "./bucket";
import type { PublicEtablissementSummary } from "./authEtablissement";

function extractPayload<T>(raw: unknown): T | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "object" && !Array.isArray(raw) && "data" in raw) {
    return (raw as { data?: T }).data;
  }
  return raw as T;
}

export async function getStatisticsPlatform(): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>("/professionnel/statistiques/platform");
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export async function getStatisticsPopulation(
  period: string
): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> {
  const q = new URLSearchParams({ period });
  const out = await apiFetch<unknown>(`/professionnel/statistiques/population?${q.toString()}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export async function getStatisticsDiseaseDistribution(
  period: string
): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> {
  const q = new URLSearchParams({ period });
  const out = await apiFetch<unknown>(`/professionnel/statistiques/disease-distribution?${q.toString()}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export async function getProfessionnelStatistics(
  nip: string
): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(
    `/professionnel/professionnels/${encodeURIComponent(nip)}/statistics`
  );
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

/** Contrat stabilisé — réponse plate triée derniereConsultation DESC. */
export type ProfessionnelPatientRow = {
  nip: string;
  nom: string;
  prenom: string;
  email: string;
  nombreConsultations: number;
  premiereConsultation: string;
  derniereConsultation: string;
};

export async function getProfessionnelPatients(
  nip: string,
  options?: { limit?: number; offset?: number }
): Promise<{ ok: true; data: ProfessionnelPatientRow[] } | { ok: false; message: string }> {
  const q = new URLSearchParams();
  if (options?.limit != null) q.set("limit", String(options.limit));
  if (options?.offset != null) q.set("offset", String(options.offset));
  const qs = q.toString();
  const out = await apiFetch<unknown>(
    `/professionnel/professionnels/${encodeURIComponent(nip)}/patients${qs ? `?${qs}` : ""}`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const raw = extractPayload<unknown>(out.data) ?? out.data;
  const list = Array.isArray(raw) ? (raw as ProfessionnelPatientRow[]) : [];
  return { ok: true, data: list };
}

export type UpdateProfessionnelBody = Record<string, unknown>;

export async function updateProfessionnel(
  nip: string,
  body: UpdateProfessionnelBody
): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`/professionnel/professionnels/${encodeURIComponent(nip)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export type ProNotification = {
  id?: string;
  titre?: string;
  title?: string;
  message?: string;
  contenu?: string;
  /** ex. 'retrait_etablissement' — un établissement a mis fin à l'affiliation de ce professionnel. */
  type?: string;
  priorite?: "basse" | "normale" | "haute";
  lienObjet?: { type?: string; id?: string; description?: string };
  statut?: string;
  read?: boolean;
  dateCreation?: string;
  [key: string]: unknown;
};

export async function getNotificationsForProfessionnel(
  professionnelNip: string,
  options?: { limit?: number; offset?: number }
): Promise<{ ok: true; data: ProNotification[] } | { ok: false; message: string }> {
  const q = new URLSearchParams();
  if (options?.limit != null) q.set("limit", String(options.limit));
  if (options?.offset != null) q.set("offset", String(options.offset));
  const qs = q.toString();
  const out = await apiFetch<unknown>(
    `/professionnel/notifications/professionnel/${encodeURIComponent(professionnelNip)}${qs ? `?${qs}` : ""}`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const raw = extractPayload<unknown>(out.data) ?? out.data;
  const list = Array.isArray(raw) ? raw : [];
  return { ok: true, data: list as ProNotification[] };
}

export async function markNotificationRead(
  notificationId: string
): Promise<{ ok: true } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`/professionnel/notifications/${encodeURIComponent(notificationId)}/mark-read`, {
    method: "PUT",
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

export async function markAllNotificationsRead(
  professionnelNip: string
): Promise<{ ok: true } | { ok: false; message: string }> {
  const q = new URLSearchParams({ professionnelNip });
  const out = await apiFetch<unknown>(`/professionnel/notifications/mark-all-read?${q.toString()}`, {
    method: "PUT",
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

// ─── Signature ────────────────────────────────────────────────────────────────

export async function uploadSignatureFile(
  file: File,
  nip: string
): Promise<{ ok: true; url: string } | { ok: false; message: string }> {
  const ext = file.name.split(".").pop() ?? "png";
  return uploadFileToBucket(file, "professionnels-signatures", `sig_${nip}.${ext}`);
}

/** PATCH /api/professionnel/:nip/signature — passer null pour effacer. */
export async function updateSignatureUrl(
  nip: string,
  signatureUrl: string | null
): Promise<{ ok: true } | { ok: false; message: string; status: number }> {
  const out = await apiFetch<unknown>(`/professionnel/${encodeURIComponent(nip)}/signature`, {
    method: "PATCH",
    body: JSON.stringify({ signature_url_link: signatureUrl }),
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  return { ok: true };
}

// ─── Établissements (liaison professionnel ↔ établissement) ───────────────────

export type LiaisonEtablissement = {
  id: string;
  professionnelNip?: string;
  etablissementId?: string;
  typeLiaison?: string;
  statut?: string;
  source?: string;
  poste?: string | null;
  dateDebut?: string | null;
  dateFin?: string | null;
  commentaire?: string | null;
  dateCreation?: string;
  dateModification?: string;
  [key: string]: unknown;
};

export type MonEtablissementItem = {
  etablissement: PublicEtablissementSummary;
  liaison: LiaisonEtablissement;
};

export type DeclareEtablissementBody = {
  etablissementId: string;
  poste?: string;
  dateDebut?: string;
  commentaire?: string;
};

/** Déclare (ou réactive/modifie) une liaison — rappeler avec le même etablissementId met à jour, ne duplique pas. Toujours 'en_attente', jamais 'active' immédiatement. */
export async function declareEtablissement(
  nip: string,
  body: DeclareEtablissementBody
): Promise<
  { ok: true; data: LiaisonEtablissement } | { ok: false; message: string; status?: number; errorCode?: string }
> {
  const out = await apiFetch<unknown>(`/professionnel/professionnels/${encodeURIComponent(nip)}/etablissements`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  const data = (extractPayload<LiaisonEtablissement>(out.data) ?? out.data) as LiaisonEtablissement;
  return { ok: true, data };
}

/** Liste des liaisons actives uniquement (pas d'historique, pas les en_attente/refusée/terminée). */
export async function getMesEtablissements(
  nip: string
): Promise<
  { ok: true; data: MonEtablissementItem[] } | { ok: false; message: string; status?: number; errorCode?: string }
> {
  const out = await apiFetch<unknown>(`/professionnel/professionnels/${encodeURIComponent(nip)}/etablissements`);
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  const raw = extractPayload<unknown>(out.data) ?? out.data;
  const list = Array.isArray(raw) ? (raw as MonEtablissementItem[]) : [];
  return { ok: true, data: list };
}

/** Soft-delete : passe la liaison à statut='terminee' (réactivable via un nouveau POST). */
export async function endEtablissementLiaison(
  nip: string,
  liaisonId: string
): Promise<
  { ok: true; data?: LiaisonEtablissement } | { ok: false; message: string; status?: number; errorCode?: string }
> {
  const out = await apiFetch<unknown>(
    `/professionnel/professionnels/${encodeURIComponent(nip)}/etablissements/${encodeURIComponent(liaisonId)}`,
    { method: "DELETE" }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  const data = extractPayload<LiaisonEtablissement>(out.data) as LiaisonEtablissement | undefined;
  return { ok: true, data };
}

export type OwnCabinet = {
  id: string;
  nom: string;
  type: string;
  email: string;
  statut: string;
  estRevendique: boolean;
  adresse?: string | null;
  telephone?: string | null;
  [key: string]: unknown;
};

export type CreateOwnCabinetBody = {
  nom: string;
  adresse?: string;
  telephone?: string;
  latitude?: number;
  longitude?: number;
};

export type CreateOwnCabinetResult = {
  etablissement: OwnCabinet;
  liaison: LiaisonEtablissement;
  /** Généré aléatoirement côté serveur, renvoyé UNE SEULE FOIS ici — jamais consultable ensuite. */
  motDePasseInitial: string;
};

/**
 * Crée un « Cabinet Médical » personnel (établissement type='cabinet_medical') pour ce
 * professionnel : liaison 'exercice' active immédiatement, pas de validation à attendre.
 * N'apparaît jamais dans l'annuaire public établissement (recherche réservée aux vrais
 * établissements revendicables).
 */
export async function createOwnCabinet(
  nip: string,
  body: CreateOwnCabinetBody
): Promise<{ ok: true; data: CreateOwnCabinetResult } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(`/professionnel/professionnels/${encodeURIComponent(nip)}/cabinets`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  const data = (extractPayload<CreateOwnCabinetResult>(out.data) ?? out.data) as CreateOwnCabinetResult;
  return { ok: true, data };
}

/** Cabinets possédés par ce professionnel (marqueur interne) — pas les liaisons vers de vrais établissements. */
export async function listOwnCabinets(
  nip: string
): Promise<{ ok: true; data: OwnCabinet[] } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(`/professionnel/professionnels/${encodeURIComponent(nip)}/cabinets`);
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  const raw = extractPayload<unknown>(out.data) ?? out.data;
  const list = Array.isArray(raw) ? (raw as OwnCabinet[]) : [];
  return { ok: true, data: list };
}

/** Envoie une ordonnance à la pharmacie sélectionnée (email côté serveur). JWT professionnel requis. */
export async function sendOrdonnanceToPharmacie(
  ordonnanceId: string,
  pharmacieId: string
): Promise<{ ok: true; message?: string } | { ok: false; message: string; status?: number }> {
  const out = await apiFetch<unknown>(
    `/professionnel/ordonnances/${encodeURIComponent(ordonnanceId)}/envoyer-pharmacie`,
    {
      method: "POST",
      body: JSON.stringify({ pharmacieId }),
    }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  const raw = out.data;
  let message: string | undefined;
  if (raw && typeof raw === "object" && !Array.isArray(raw) && "message" in raw) {
    const m = (raw as { message?: unknown }).message;
    if (typeof m === "string" && m.trim()) message = m;
  }
  return { ok: true, message };
}
